import { useCallback, useEffect, useState } from 'react';
import { useResendVerificationEmailMutation } from '../../../../domainsApi';

const COOLDOWN_SECONDS = 60;

interface UseEmailResendResult {
  handleResendEmail: () => void;
  isResending: boolean;
  cooldownSeconds: number;
  canResend: boolean;
}

export const useEmailResend = (storeId: string): UseEmailResendResult => {
  const [resendEmail, { isLoading: isResending }] =
    useResendVerificationEmailMutation();
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleResendEmail = useCallback(async () => {
    if (cooldownSeconds > 0 || isResending) return;

    try {
      await resendEmail(storeId).unwrap();
      setCooldownSeconds(COOLDOWN_SECONDS);
    } catch (error) {
      setCooldownSeconds(COOLDOWN_SECONDS);
    }
  }, [cooldownSeconds, isResending, resendEmail, storeId]);

  const canResend = cooldownSeconds === 0 && !isResending;

  return {
    handleResendEmail,
    isResending,
    cooldownSeconds,
    canResend,
  };
};
